import React, { useState } from 'react';
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { RefreshCw, PauseCircle, Info, ChevronDown, ChevronUp, Wallet, ShieldCheck, Users, ChevronsDown, ChevronsUp } from 'lucide-react';
import {
  ActivationGate, VaultHeader, SuccessorsPanel, VaultSummaryPanel,
} from './EstateVaultPanelParts';
import FAQLink from './faqs/FAQLink';
import { useEstateVaultActions } from './estate-vault/useEstateVaultActions';
import { ContinuityAssetsPanel } from './estate-vault/ContinuityAssetsPanel';
import PersonalContinuityAssetsPanel from './continuity-assets/ContinuityAssetsPanel';
import { VaultSummaryWalkthrough } from './estate-vault/VaultSummaryWalkthrough';

/** Plain-English notice when the vault has been auto-paused because the
 *  owner has fewer than 3 Trusted Contacts (Feb 2026 quorum redesign, Stage C). */
function LowContactPauseBanner({ minSuccessors }) {
  const min = minSuccessors || 3;
  return (
    <div
      className="border border-amber-300 bg-amber-50 rounded-lg p-4 text-sm text-amber-900 flex items-start gap-3"
      data-testid="ev-paused-low-contacts-banner"
    >
      <PauseCircle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
      <div>
        <div className="font-semibold mb-1">Your Continuity System is paused for safe-keeping</div>
        <p className="leading-relaxed">
          The rules changed recently &mdash; we now need any <strong>2 of {min}</strong> Trusted Contacts to confirm before your record is ever released.
          Because you currently have fewer than {min} Trusted Contacts, we&rsquo;ve quietly paused the system so nothing can move forward until you&rsquo;re set up properly.
        </p>
        <p className="leading-relaxed mt-1.5">
          <strong>Nothing is lost.</strong> Everything you&rsquo;ve added stays exactly as it is. The moment you add a {min === 3 ? 'third' : `${min}th`} Trusted Contact below, we&rsquo;ll switch things back on automatically.
        </p>
      </div>
    </div>
  );
}

/** Plain-English copy explaining the check-in cycle to the owner. Renders
 *  as a compact chip that expands on click — visible on first render but
 *  quietly stays out of the way after the user has read it once. */
function CheckInCycleCallout({ state }) {
  const months = Math.round((state.checkin_interval_days || 90) / 30);
  const [open, setOpen] = useState(false);
  return (
    <div className="flex justify-start">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-200 bg-emerald-50/60 hover:bg-emerald-50 transition-colors text-xs text-emerald-800"
        data-testid="ev-checkin-chip"
        aria-expanded={open}
      >
        <Info className="w-3 h-3" />
        <span className="font-medium">How the check-in cycle works</span>
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {open && (
        <div className="ml-2 max-w-2xl text-xs text-slate-700 leading-relaxed" data-testid="ev-checkin-detail">
          Every {months} months we send you a short &ldquo;I&rsquo;m well&rdquo; check-in. Respond and nothing happens. If you don&rsquo;t respond, we send progressive reminders over the following months. Only after several missed check-ins, and only after two of our staff manually verify the situation, will the people you named receive your Continuity Records. You can pause or turn this off at any time.
        </div>
      )}
    </div>
  );
}

/** Audit-log dialog rendered when the owner opens the activity log. */
function AuditDialog({ open, onOpenChange, auditRows }) {
  if (!open) return null;
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Continuity System activity log</AlertDialogTitle>
          <AlertDialogDescription>Every action you or our team takes on your Continuity System is recorded here for your records.</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="max-h-96 overflow-y-auto text-xs space-y-2" data-testid="ev-audit-list">
          {auditRows.length === 0 && <div className="text-slate-500">No events yet.</div>}
          {auditRows.map((ev) => (
            <div key={ev._id || ev.created_at} className="border-b border-slate-100 pb-1">
              <div className="text-slate-800 font-medium">{ev.action} <span className="text-slate-400 font-normal">· {ev.actor_type}</span></div>
              <div className="text-slate-500">{new Date(ev.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="ev-audit-close">Close</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

const EstateVaultPanel = () => {
  const a = useEstateVaultActions();
  const [invCount, setInvCount] = useState(null);
  const [assetsCount, setAssetsCount] = useState(null);
  const [activeSection, setActiveSection] = useState('investments');
  // On phone widths, the intro block (Welcome hero + Continuity Records)
  // pushed the sub-tabs ~780px below the fold. We collapse it by default on
  // mobile so the sub-tabs are the first thing a user sees on iPhone SE.
  // On tablet+ (sm:), the intro is always visible regardless of this state.
  const [introOpen, setIntroOpen] = useState(false);

  if (a.loading) {
    return (
      <div className="bg-white rounded-xl shadow-xs border border-slate-200 p-4 sm:p-6">
        <div className="flex items-center gap-2 text-slate-500"><RefreshCw className="w-4 h-4 animate-spin" /> Loading Continuity System…</div>
      </div>
    );
  }
  if (!a.state) return null;

  // Activation gate — vault never started or has been revoked.
  if (!a.state.exists || a.state.status === 'revoked') {
    return (
      <div className="space-y-3" data-testid="estate-vault-activation-wrapper">
        <ActivationGate
          state={a.state} consents={a.consents} setConsents={a.setConsents}
          submitting={a.submitting} handleActivate={a.handleActivate}
        />
        <div className="pt-1">
          <FAQLink categoryKey="estate-vault" />
        </div>
      </div>
    );
  }

  // Active / paused / triggered / released — full panel.
  const status = a.state.status;
  const canEdit = status === 'active' || status === 'paused';
  // Show Continuity Records only if the vault has something meaningful to summarise
  // (a Trusted Contact or a Vault Asset already catalogued as an investment).
  // Snapshots on their own don't count — a fresh user has one auto-generated
  // summary from activation, but nothing in it yet.
  const hasVaultContent = (a.state.successor_count || 0) > 0
    || (a.state.investment_count || 0) > 0;

  return (
    <div className="bg-white rounded-xl shadow-xs border border-slate-200 p-4 sm:p-6 space-y-6" data-testid="estate-vault-panel">
      {/* Mobile-only toggle. On phone widths (<sm), the intro block below is
          hidden by default; tap to reveal the Welcome hero, status, action
          buttons, and Continuity Records card. Always hidden on tablet+. */}
      <button
        type="button"
        onClick={() => setIntroOpen((v) => !v)}
        className="sm:hidden w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 transition text-left"
        data-testid="cs-mobile-intro-toggle"
        aria-expanded={introOpen}
        aria-controls="cs-mobile-intro-block"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-slate-800">
          <Info className="w-4 h-4 text-emerald-700 shrink-0" />
          About your Continuity System
          <span className={`ml-1 text-[11px] px-2 py-0.5 rounded-full capitalize ${
            status === 'active' ? 'bg-emerald-100 text-emerald-800'
              : status === 'paused' ? 'bg-amber-100 text-amber-800'
              : 'bg-slate-200 text-slate-700'
          }`}>{status}</span>
        </span>
        {introOpen ? <ChevronsUp className="w-4 h-4 text-slate-500" /> : <ChevronsDown className="w-4 h-4 text-slate-500" />}
      </button>
      <div
        id="cs-mobile-intro-block"
        className={`${introOpen ? 'block' : 'hidden'} sm:block space-y-6`}
        data-testid="cs-intro-block"
      >
        <VaultHeader
          state={a.state} status={status}
          handlePause={a.handlePause} handleResume={a.handleResume}
          handleCheckinRespond={a.handleCheckinRespond}
          loadAudit={a.loadAudit} handleRevoke={a.handleRevoke}
        />
        {status === 'paused' && a.state.pause_reason === 'low_contacts' && (
          <LowContactPauseBanner minSuccessors={a.state.min_successors} />
        )}
        <CheckInCycleCallout state={a.state} />
        <VaultSummaryPanel
          snapshots={a.snapshots || []}
          handlePreview={a.handlePreview}
          handleGenerateSnapshot={a.handleGenerateSnapshot}
          submitting={a.submitting} canEdit={canEdit}
          hasVaultContent={hasVaultContent}
        />
      </div>
      {/* Sub-tabs: one section visible at a time so a founder with 50+
          investments no longer has to scroll past every card to reach the
          Continuity Assets or Trusted Contacts sections. Styled prominently
          — full-width grid, emerald active state, larger touch targets —
          so first-time users can't miss the navigation. */}
      <Tabs value={activeSection} onValueChange={setActiveSection} className="w-full" data-testid="cs-subtabs">
        <TabsList className="grid grid-cols-1 sm:grid-cols-3 w-full bg-slate-100 p-1.5 gap-1.5 h-auto rounded-lg border border-slate-200">
          <TabsTrigger
            value="investments"
            data-testid="cs-subtab-investments"
            className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:font-semibold py-2.5 px-4 text-sm sm:text-base font-medium text-slate-700 hover:text-slate-900 transition"
          >
            <Wallet className="w-4 h-4 mr-2" />
            Portfolio Investments{invCount !== null ? ` (${invCount})` : ''}
          </TabsTrigger>
          <TabsTrigger
            value="assets"
            data-testid="cs-subtab-assets"
            className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:font-semibold py-2.5 px-4 text-sm sm:text-base font-medium text-slate-700 hover:text-slate-900 transition"
          >
            <ShieldCheck className="w-4 h-4 mr-2" />
            Continuity Assets{assetsCount !== null ? ` (${assetsCount})` : ''}
          </TabsTrigger>
          <TabsTrigger
            value="contacts"
            data-testid="cs-subtab-contacts"
            className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:font-semibold py-2.5 px-4 text-sm sm:text-base font-medium text-slate-700 hover:text-slate-900 transition"
          >
            <Users className="w-4 h-4 mr-2" />
            Trusted Contacts ({a.state.successor_count || 0})
          </TabsTrigger>
        </TabsList>
        <TabsContent value="investments" className="mt-4" forceMount>
          <div hidden={activeSection !== 'investments'}>
            <ContinuityAssetsPanel onCountChange={setInvCount} />
          </div>
        </TabsContent>
        <TabsContent value="assets" className="mt-4" forceMount>
          <div hidden={activeSection !== 'assets'}>
            <PersonalContinuityAssetsPanel onCountChange={setAssetsCount} />
          </div>
        </TabsContent>
        <TabsContent value="contacts" className="mt-4" forceMount>
          <div hidden={activeSection !== 'contacts'}>
            <SuccessorsPanel
              state={a.state} canEdit={canEdit}
              handleRemoveSuccessor={a.handleRemoveSuccessor}
              handlePreviewSuccessor={a.handlePreviewSuccessor}
              handleAddSuccessor={a.handleAddSuccessor}
              handleUpdateTrustedContact={a.handleUpdateTrustedContact}
              newSucc={a.newSucc} setNewSucc={a.setNewSucc}
              submitting={a.submitting}
            />
          </div>
        </TabsContent>
      </Tabs>
      <VaultSummaryWalkthrough
        vaultId={a.state.vault_id}
        successorCount={a.state.successor_count}
      />
      <div className="pt-1">
        <FAQLink categoryKey="estate-vault" />
      </div>
      <AuditDialog open={a.auditOpen} onOpenChange={a.setAuditOpen} auditRows={a.auditRows} />
    </div>
  );
};

export default EstateVaultPanel;
